import { formatDateBR } from "../../utils/format";
import InfoHelp from "../../components/help/InfoHelp";
import { metricHelp } from "../../components/help/helpText";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import ManualActionPlanBuilder from "./ManualActionPlanBuilder";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  Link2,
  Loader2,
  MapPin,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Upload,
  WandSparkles,
} from "lucide-react";

const stateLabel = {
  vencida: "Vencida",
  vence_7d: "Até 7 dias",
  vence_30d: "Até 30 dias",
  vence_60d: "Até 60 dias",
  sem_prazo: "Sem prazo",
  ok: "Regular",
  futura: "Futura",
};

const statusLabel = {
  pendente: "Pendente",
  em_andamento: "Em andamento",
  evidencia_enviada: "Evidência enviada",
  em_analise: "Em análise",
  executada: "Executada",
  validada: "Validada",
  recusada: "Recusada",
  cancelada: "Cancelada",
  substituida: "Substituída",
};

function Metric({ icon: Icon, label, value, hint, danger = false }) {
  return (
    <Card className="min-w-0 border-border/75">
      <CardContent className="min-w-0 p-4 sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium leading-snug text-muted-foreground">
              {label}<InfoHelp label={label}>{metricHelp[label] || hint || "Indicador calculado para os registros e filtros desta tela."}</InfoHelp>
            </div>
            <div
              className={`mt-2 font-mono text-2xl font-semibold leading-none tabular-nums ${danger ? "text-destructive" : "text-foreground"}`}
            >
              {value}
            </div>
            {hint && (
              <div className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {hint}
              </div>
            )}
          </div>
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OperationsHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const availableTabs = ["hoje", "acoes", "manual", "importar", "agenda", "visitas"];
  const selectedTab = availableTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "acoes";
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [clientFilter, setClientFilter] = useState(searchParams.get("client_id") || "all");
  const [importClient, setImportClient] = useState(searchParams.get("client_id") || "");
  const [revision, setRevision] = useState("");
  const [sourceFile, setSourceFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [visit, setVisit] = useState({
    client_id: searchParams.get("client_id") || "",
    titulo: "Visita técnica",
    scheduled_at: "",
    objetivo: "",
    responsavel: "",
  });
  const [portalLink, setPortalLink] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [o, c, a, ag, v] = await Promise.all([
        api.get("/operations/overview"),
        api.get("/clients"),
        api.get("/operations/actions?limit=500"),
        api.get("/operations/agenda?days=90"),
        api.get("/operations/visits"),
      ]);
      setOverview(o.data);
      setClients(c.data);
      setActions(a.data);
      setAgenda(ag.data);
      setVisits(v.data);
    } catch (e) {
      setLoadError("Não foi possível consultar os registros. Os dados exibidos podem estar incompletos; tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c.razao_social])),
    [clients],
  );
  const visibleActions =
    clientFilter === "all"
      ? actions
      : actions.filter((a) => a.client_id === clientFilter);

  const importPreview = async () => {
    if (!importClient || !sourceFile)
      return toast.error("Selecione cliente e arquivo do PGR");
    setImporting(true);
    setPreview(null);
    const fd = new FormData();
    fd.append("client_id", importClient);
    fd.append("source_revision", revision);
    fd.append("file", sourceFile);
    try {
      const r = await api.post("/operations/action-plans/import-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      if (!r.data.actions?.length)
        toast.warning(
          "Nenhuma ação foi identificada automaticamente. Revise o documento ou cadastre ações manualmente.",
        );
      else
        toast.success(
          `${r.data.actions.length} ação(ões) identificada(s) para revisão`,
        );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao analisar PGR");
    } finally {
      setImporting(false);
    }
  };

  const commitImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      await api.post("/operations/action-plans", {
        client_id: importClient,
        nome: `Plano de Ação do PGR${revision ? ` ${revision}` : ""}`,
        origem: "PGR",
        source_revision: revision,
        source_filename: preview.source_filename,
        source_document_hash: preview.source_document_hash,
        items: preview.actions,
      });
      toast.success(
        "Plano de ação importado. O texto original foi preservado.",
      );
      setPreview(null);
      setSourceFile(null);
      setRevision("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao confirmar importação");
    } finally {
      setImporting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/operations/actions/${id}`, { status });
      toast.success("Ação atualizada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar ação");
    }
  };

  const rotatePortal = async (clientId, replace = false) => {
    if (
      replace &&
      !window.confirm(
        "Substituir o link do cliente? O anterior deixará de funcionar, inclusive nos grupos de WhatsApp.",
      )
    )
      return;
    try {
      const r = await api.post(
        `/operations/client-portal/${clientId}/${replace ? "rotate" : "link"}`,
      );
      setPortalLink(r.data.url);
      if (navigator.clipboard)
        await navigator.clipboard.writeText(r.data.url).catch(() => {});
      toast.success(
        "Link do cliente criado e copiado quando permitido pelo navegador",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar link");
    }
  };

  const createVisit = async () => {
    if (!visit.client_id) return toast.error("Selecione o cliente");
    try {
      await api.post("/operations/visits", {
        ...visit,
        scheduled_at: visit.scheduled_at || null,
      });
      toast.success("Visita incluída na agenda");
      setVisit({
        client_id: "",
        titulo: "Visita técnica",
        scheduled_at: "",
        objetivo: "",
        responsavel: "",
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar visita");
    }
  };

  return (
    <>
      <Header
        title={selectedTab === "visitas" ? "Visitas técnicas" : "Planos de ação"}
        subtitle={selectedTab === "visitas" ? "Planeje e registre as visitas técnicas aos clientes." : "Planos do PGR e de outras avaliações: ações, prazos e comprovações por cliente."}
        actions={
          <Button variant="outline" onClick={load}>
            <RefreshCcw className="size-4" /> Atualizar
          </Button>
        }
      />
      <div className="page-container space-y-6" data-testid="operations-hub">
        {loadError && <div role="alert" className="rounded-xl border border-destructive/40 p-4">{loadError} <Button variant="outline" onClick={load}>Tentar novamente</Button></div>}
        {loading && (
          <div className="h-28 animate-pulse rounded-2xl border border-border/70 bg-card/50" />
        )}
        {!loading && overview && (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              icon={AlertTriangle}
              label="Ações vencidas"
              value={overview.deadlines?.vencida || 0}
              hint={`${overview.deadlines?.vence_7d || 0} vencem em 7 dias`}
              danger={(overview.deadlines?.vencida || 0) > 0}
            />
            <Metric
              icon={ShieldCheck}
              label="Evidências para conferir"
              value={overview.evidence_pending || 0}
              hint="Enviadas pelos clientes"
            />
            <Metric
              icon={ClipboardCheck}
              label="Pendências da equipe"
              value={overview.tasks_pending || 0}
              hint="Tarefas internas em aberto"
            />
            <Metric
              icon={MapPin}
              label="Visitas em aberto"
              value={overview.upcoming_visits || 0}
              hint="Planejadas ou em execução"
            />
          </div>
        )}

        <Tabs value={selectedTab} onValueChange={(value) => { const next = new URLSearchParams(searchParams); next.set("tab", value); setSearchParams(next); }}>
          <TabsList className="w-full justify-start overflow-x-auto">
            {selectedTab === "visitas" ? <TabsTrigger value="visitas">Visitas técnicas</TabsTrigger> : <>
              <TabsTrigger value="acoes">Ações do plano</TabsTrigger>
              <TabsTrigger value="manual">Criar plano manual</TabsTrigger>
              <TabsTrigger value="importar">Importar do PGR</TabsTrigger>
            </>}
          </TabsList>

          <TabsContent value="hoje" className="mt-5 space-y-4">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground">
                    Por onde começar
                  </div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Ações mais urgentes
                  </h2>
                </div>
                {!overview?.recommended_focus?.length ? (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma ação crítica ou vencida no momento.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {overview.recommended_focus.map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-col gap-3 rounded-xl border border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {item.title}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {clientMap[item.client_id] || "Cliente"} ·{" "}
                            {stateLabel[item.deadline_state] ||
                              item.deadline_state}
                          </div>
                        </div>
                        <Link to={`/clientes/${item.client_id}/360`}>
                          <Button size="sm" variant="outline">
                            Abrir cliente <ArrowRight className="size-3.5" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="acoes" className="mt-5 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  Acompanhamento
                </div>
                <h2 className="mt-1 text-lg font-semibold">
                  Ações do plano
                </h2>
              </div>
              <div className="w-full sm:w-72">
                <Select
                  value={clientFilter}
                  onValueChange={(value) => {
                    setClientFilter(value);
                    setPortalLink("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3">
              {visibleActions.map((a) => (
                <Card
                  key={a.id}
                  className={
                    a.deadline_state === "vencida"
                      ? "border-destructive/35"
                      : ""
                  }
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={
                              a.deadline_state === "vencida"
                                ? "destructive"
                                : "outline"
                            }
                          >
                            {stateLabel[a.deadline_state] || a.deadline_state}
                          </Badge>
                          <Badge variant="secondary">
                            {statusLabel[a.status] || a.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {clientMap[a.client_id]}
                          </span>
                        </div>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">
                          {a.texto_original}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                          <span>Prazo: {formatDateBR(a.next_due_at || a.prazo) || "Sem prazo"}</span>
                          <span>Responsável: {a.responsavel || "—"}</span>
                          <span>Prioridade: {{ alta: "Alta", media: "Média", baixa: "Baixa" }[a.prioridade] || a.prioridade}</span>
                          {a.norma && <span>{a.norma}</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 xl:w-64 xl:justify-end">
                        <Link to={`/clientes/${a.client_id}/360`}>
                          <Button variant="outline" size="sm">
                            Abrir cliente
                          </Button>
                        </Link>
                        {a.status !== "validada" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(a.id, "em_andamento")}
                          >
                            Em andamento
                          </Button>
                        )}
                        {a.status !== "validada" && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(a.id, "executada")}
                          >
                            Marcar executada
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {visibleActions.length === 0 && (
                <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                  Nenhuma ação cadastrada neste filtro.
                </div>
              )}
            </div>

            <details className="rounded-xl border border-border/70 bg-card p-4">
              <summary className="cursor-pointer text-sm font-semibold">
                Enviar link para o cliente anexar evidências
              </summary>
              <p className="mt-2 text-xs text-muted-foreground">
                Selecione um cliente acima. O link é estável e pode ser salvo no
                WhatsApp. Mostra somente ações deste cliente.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  variant="outline"
                  disabled={clientFilter === "all"}
                  onClick={() => rotatePortal(clientFilter)}
                >
                  <Link2 className="size-4" /> Copiar link do cliente
                </Button>
                <Button
                  variant="outline"
                  disabled={clientFilter === "all"}
                  onClick={() => rotatePortal(clientFilter, true)}
                >
                  Substituir e revogar anterior
                </Button>
                {portalLink && (
                  <Input className="flex-1" readOnly value={portalLink} />
                )}
              </div>
            </details>
          </TabsContent>

          <TabsContent value="manual" className="mt-5">
            <ManualActionPlanBuilder
              clients={clients}
              existingActions={actions}
              onSaved={load}
            />
          </TabsContent>

          <TabsContent value="importar" className="mt-5">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-5 flex items-start gap-3">
                  <div className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                    <WandSparkles className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">
                      Importar ações de um PGR
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Envie o PGR, confira as ações encontradas e só então
                      confirme a importação.
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label>Cliente</Label>
                    <Select
                      value={importClient}
                      onValueChange={setImportClient}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Revisão do documento</Label>
                    <Input
                      value={revision}
                      onChange={(e) => setRevision(e.target.value)}
                      placeholder="Ex.: R01"
                    />
                  </div>
                  <div>
                    <Label>Arquivo PGR</Label>
                    <Input
                      type="file"
                      accept=".pdf,.docx,.txt,.md,.csv"
                      onChange={(e) =>
                        setSourceFile(e.target.files?.[0] || null)
                      }
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={importPreview} disabled={importing}>
                    {importing ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Upload className="size-4" />
                    )}{" "}
                    Ler ações do PGR
                  </Button>
                </div>
                {preview && (
                  <div className="mt-6 space-y-4 border-t border-border pt-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold">
                          Confira antes de importar
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {preview.actions.length} ação(ões) · SHA-256{" "}
                          {preview.source_document_hash?.slice(0, 16)}…
                        </div>
                      </div>
                      <Button
                        onClick={commitImport}
                        disabled={importing || !preview.actions.length}
                      >
                        <CheckCircle2 className="size-4" /> Confirmar importação
                      </Button>
                    </div>
                    {preview.warnings?.map((w, i) => (
                      <div
                        key={i}
                        className="rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                      >
                        {w}
                      </div>
                    ))}
                    <div className="space-y-2">
                      {preview.actions.map((a, i) => (
                        <div
                          key={`${a.texto_original}-${i}`}
                          className="rounded-xl border border-border/70 p-4"
                        >
                          <div className="text-xs font-medium text-muted-foreground">
                            Ação {i + 1}
                          </div>
                          <div className="mt-2 text-sm">{a.texto_original}</div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {a.responsavel || "Responsável não identificado"} ·{" "}
                            {formatDateBR(a.prazo) || "Prazo não identificado"} ·{" "}
                            {a.source_locator || "Localização não informada"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="agenda" className="mt-5">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground">
                    Próximos 90 dias
                  </div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Agenda de ações e visitas
                  </h2>
                </div>
                <div className="space-y-2">
                  {agenda.map((e, i) => (
                    <div
                      key={`${e.type}-${e.id}-${i}`}
                      className="flex items-center gap-4 rounded-xl border border-border/60 p-3"
                    >
                      <div className="w-24 shrink-0 font-mono text-xs">
                        {e.date || "—"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {e.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {clientMap[e.client_id] || "Interno"} · {e.type}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!agenda.length && (
                    <p className="text-sm text-muted-foreground">
                      Nenhum compromisso registrado para o período.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="visitas" className="mt-5 space-y-4">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground">
                    Nova visita
                  </div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Agendar visita técnica
                  </h2>
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <Label>Cliente</Label>
                    <Select
                      value={visit.client_id}
                      onValueChange={(v) =>
                        setVisit({ ...visit, client_id: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data / hora</Label>
                    <Input
                      type="datetime-local"
                      value={visit.scheduled_at}
                      onChange={(e) =>
                        setVisit({ ...visit, scheduled_at: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={visit.titulo}
                      onChange={(e) =>
                        setVisit({ ...visit, titulo: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Responsável</Label>
                    <Input
                      value={visit.responsavel}
                      onChange={(e) =>
                        setVisit({ ...visit, responsavel: e.target.value })
                      }
                    />
                  </div>
                  <div className="md:col-span-2 xl:col-span-4">
                    <Label>Objetivo</Label>
                    <Textarea
                      value={visit.objetivo}
                      onChange={(e) =>
                        setVisit({ ...visit, objetivo: e.target.value })
                      }
                      placeholder="Ex.: verificar pendências do PGR, coletar evidências e inspecionar adequações NR-12"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={createVisit}>
                    <Plus className="size-4" /> Agendar visita
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="grid gap-3 lg:grid-cols-2">
              {visits.map((v) => (
                <Card key={v.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{v.titulo}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {clientMap[v.client_id]} ·{" "}
                          {v.scheduled_at || "Sem data"}
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {v.objetivo || "Sem objetivo registrado"}
                        </p>
                      </div>
                      <Link to={`/operacoes/visitas/${v.id}`}>
                        <Button variant="outline" size="sm">
                          Abrir visita
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
